import { Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { approvalApi } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import Logo from '../Logo';
import NotificationBell from '../NotificationBell';
import { navSections, dashboardItem, agentItem, organizationItem, footerItems, getActiveNavHref, buildTieredNav } from './navConfig';
import SidebarNavItem from './SidebarNavItem';
import SidebarNavGroup from './SidebarNavGroup';
import SidebarUpgradeGroup from './SidebarUpgradeGroup';
import SidebarFooter from './SidebarFooter';
import TeamSwitcher from './TeamSwitcher';
import ActiveBankIndicator from './ActiveBankIndicator';
import RouteModuleIntro from '../help/RouteModuleIntro';
import { useTeamSwitcher } from '../../hooks/useTeamId';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

function SidebarContent({
  pendingCount,
  showOrganization,
}: {
  pendingCount: number;
  showOrganization: boolean;
}) {
  // Tier-aware nav (UX epic PR 7): accessible items render in their functional
  // sections (the bank's specialty section leading); everything locked is
  // consolidated into the single "Unlock more" surface below.
  const { hasFeature } = useFeatureAccess();
  const { sections, locked } = buildTieredNav(hasFeature);
  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center px-5 border-b border-rule">
        <Link to="/dashboard">
          <Logo className="h-9 w-auto" />
        </Link>
      </div>

      {/* Team switcher — visible only when the user belongs to >1 team */}
      <TeamSwitcher />

      {/* Active bank identity — name + LEI, always visible (PR 11) */}
      <ActiveBankIndicator />

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {/* Organization — multi-bank users only. Above Dashboard so
            consultants land here first ("which bank today?" → drill down). */}
        {showOrganization && <SidebarNavItem item={organizationItem} />}

        {/* Dashboard — always visible */}
        <SidebarNavItem item={dashboardItem} />
        <SidebarNavItem item={agentItem} />

        <div className="pt-3 space-y-1">
          {sections.map(({ section, items }) => (
            <SidebarNavGroup
              key={section.id}
              section={section}
              items={items}
              pendingCount={pendingCount}
            />
          ))}
          <SidebarUpgradeGroup locked={locked} />
        </div>
      </nav>

      {/* Footer */}
      <SidebarFooter />
    </div>
  );
}

export default function SidebarLayout({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuthStore();
  const location = useLocation();
  const { teams, activeTeamId } = useTeamSwitcher();
  const activeTeam = teams.find((t) => t.id === activeTeamId) ?? teams[0];

  // Organization nav item visibility: only users with access to more than one
  // bank/CU see it. Single-bank customers (the majority) never see it. Channel
  // partners and consultants — exactly the multi-bank users — see it on first
  // load. Hidden again if they ever drop to one bank.
  const showOrganization = teams.length > 1;

  // Pending reviews badge (same query as old Layout)
  const { data: pendingData } = useQuery({
    queryKey: ['pending-reviews-count'],
    queryFn: async () => {
      const response = await approvalApi.getPendingReviews(1);
      return response.data;
    },
    refetchInterval: 60000,
  });
  const pendingCount = pendingData?.count || 0;

  // Derive current page title from nav config for the top bar.
  // Uses longest-prefix match so child routes show their own item name, not the parent.
  const currentPageTitle = (() => {
    const activeHref = getActiveNavHref(location.pathname);
    if (!activeHref) return '';
    if (activeHref === dashboardItem.href) return dashboardItem.name;
    if (activeHref === agentItem.href) return agentItem.name;
    if (activeHref === organizationItem.href) return organizationItem.name;
    for (const section of navSections) {
      for (const item of section.items) {
        if (item.href === activeHref) return item.name;
      }
    }
    for (const item of footerItems) {
      if (item.href === activeHref) return item.name;
    }
    return '';
  })();

  // On /teams/:id, the URL points at a SPECIFIC bank, but the breadcrumb's
  // second segment used to always say `activeTeam.name` — which made it look
  // like you were viewing the active team even when you'd navigated into a
  // different one. Resolve from the URL when we're on a team detail page so
  // the breadcrumb matches what the page actually shows.
  const teamDetailMatch = location.pathname.match(/^\/teams\/([^/]+)$/);
  const breadcrumbTeamName = (() => {
    if (teamDetailMatch) {
      const urlTeamId = teamDetailMatch[1];
      const urlTeam = teams.find((t) => t.id === urlTeamId);
      if (urlTeam) return urlTeam.name;
    }
    return activeTeam?.name ?? null;
  })();

  return (
    <div className="h-screen flex bg-paper overflow-hidden">
      {/* Desktop sidebar — flex sibling of scroll area, cannot scroll with content */}
      <div className="hidden lg:flex lg:w-72 lg:flex-col lg:flex-shrink-0 border-r border-rule bg-panel">
        <SidebarContent pendingCount={pendingCount} showOrganization={showOrganization} />
      </div>

      {/* Mobile sidebar — slide-over */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileMenuOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-150 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                {/* Close button */}
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col bg-panel" onClick={() => setMobileMenuOpen(false)}>
                  <SidebarContent pendingCount={pendingCount} showOrganization={showOrganization} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Main content column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — flex sibling of scroll area, cannot scroll with content */}
        <div className="z-30 flex h-14 shrink-0 items-center border-b border-rule bg-panel/80 backdrop-blur-sm px-4 sm:px-6 lg:px-8">
          {/* Mobile hamburger */}
          <button
            type="button"
            className="lg:hidden -ml-1 mr-3 inline-flex items-center justify-center rounded-sm p-2 text-ink-3 hover:bg-panel-2 hover:text-ink"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Page title (desktop) */}
          <div className="flex-1 min-w-0 flex items-center gap-3">
            <h2 className="text-sm font-semibold text-ink truncate">
              {currentPageTitle}
            </h2>
            {breadcrumbTeamName && (
              <>
                <span className="hidden sm:inline text-rule">/</span>
                <span className="hidden sm:inline text-sm text-ink-3 truncate">
                  {breadcrumbTeamName}
                </span>
              </>
            )}
          </div>

          {/* Right side: subscription badge + notifications */}
          <div className="flex items-center gap-3">
            {user?.subscriptionPlan && (
              <span
                className={clsx(
                  // Brand "pill": mono, uppercase, tracked, tight 2px corner —
                  // matches the marketing design system. Purple was off-brand.
                  'hidden sm:inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider',
                  user.subscriptionPlan === 'ENTERPRISE'
                    ? 'bg-ink text-paper'
                    : user.subscriptionPlan === 'FAIR_LENDING' || user.subscriptionPlan === 'CRA'
                      ? 'bg-teal-100 text-teal-700'
                      : user.subscriptionPlan === 'CORE' || user.subscriptionPlan === 'PROFESSIONAL'
                        ? 'bg-panel-2 text-ink-3'
                        : 'bg-panel-2 text-ink-4'
                )}
              >
                {user.subscriptionPlan === 'ENTERPRISE'
                  ? 'Enterprise'
                  : user.subscriptionPlan === 'FAIR_LENDING'
                    ? 'Fair Lending'
                    : user.subscriptionPlan === 'CRA'
                      ? 'CRA'
                      : user.subscriptionPlan === 'CORE'
                        ? 'Core'
                        : user.subscriptionPlan === 'PROFESSIONAL'
                          ? 'Professional'
                          : 'Free'}
              </span>
            )}
            <NotificationBell />
          </div>
        </div>

        {/* Page content — the only scrollable element in the app shell */}
        <main id="app-scroll-container" className="flex-1 overflow-y-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <RouteModuleIntro pathname={location.pathname} />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
